'use client';
import type React from 'react';
import {useGoogleMapsApiKey} from '#/features/store/clientEnvStore';
import {useCurrentThemeColors, useThemeStore} from '#/features/store/themeColorsStore';
import {darkMapStyle} from '#/styles/darkMapStyle';
import {type SearchRadius} from '#/utils/geo';
import {GoogleMap, useJsApiLoader} from '@react-google-maps/api';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {MapSearchRadius} from './MapSearchRadius';
import {useWebAppInfoStore} from '../store/webAppInfoStore';

const defaultMapConstants = {
  zoom: 13,
  center: {lat: 0, lng: 0},
  mapPadding: 40,
};

const containerStyle = {
  flex: 1,
};

const containerStyleFullHeight = {
  flex: 1,
  height: '100%',
};

export type MapProps = {
  boundsLatLngs?: google.maps.LatLngLiteral[];
  center?: google.maps.LatLngLiteral;
  Markers?: React.ReactElement;
  searchRadius?: SearchRadius;
  onSearchRadiusCenterChanged?: (center: google.maps.LatLngLiteral) => void;
  onSearchRadiusRadiusChanged?: (radius: number) => void;
  full?: boolean;
};

export const Map = (props: MapProps) => {
  const googleMapsApiKey = useGoogleMapsApiKey();
  if (!googleMapsApiKey) return null;
  return <InnerMap {...props} googleMapsApiKey={googleMapsApiKey} />;
};

const InnerMap = (props: MapProps & {googleMapsApiKey: string}) => {
  const {
    center,
    Markers,
    boundsLatLngs,
    googleMapsApiKey,
    searchRadius,
    onSearchRadiusCenterChanged,
    onSearchRadiusRadiusChanged,
    full = false,
  } = props;

  // set the project's default location
  const {defaultLocation} = useWebAppInfoStore();
  const mapConstants = useMemo(() => {
    if (defaultLocation) {
      const {latitude, longitude} = defaultLocation;
      return {...defaultMapConstants, center: {lat: latitude, lng: longitude}};
    } else {
      return defaultMapConstants;
    }
  }, [defaultLocation]);

  const theme = useThemeStore();
  const currentThemeColors = useCurrentThemeColors();
  const mapRef = useRef<google.maps.Map | null>(null);

  const {isLoaded} = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleMapsApiKey,
  });

  /**
   * Map options based on the current theme
   */
  const mapOptions: google.maps.MapOptions = useMemo(() => {
    return {
      disableDefaultUI: true,
      styles: theme.themeColorsVariant === 'dark' ? darkMapStyle : undefined,
      backgroundColor: currentThemeColors.midground2,
      isFractionalZoomEnabled: true,
      clickableIcons: false,
    };
  }, [currentThemeColors, theme]);

  /**
   * Fit the map to the search radius bounds
   */
  const fitToSearchRadiusBounds = useCallback(
    (map: google.maps.Map) => {
      if (mapRef.current && searchRadius) {
        const bounds = new window.google.maps.Circle({
          center: searchRadius.center,
          radius: searchRadius.radius,
        }).getBounds();
        if (bounds) {
          map.fitBounds(bounds, mapConstants.mapPadding * 2);
        }
      }
    },
    [mapConstants.mapPadding, searchRadius]
  );

  /**
   * Fit the map to the boundsLatLngs bounds
   */
  const fitToBoundsLatLngs = useCallback(
    (map: google.maps.Map) => {
      if (mapRef.current && boundsLatLngs) {
        const bounds = new window.google.maps.LatLngBounds();
        const allTheSame = boundsLatLngs?.every(
          (location, i, locations) => location.lat === locations[0]?.lat && location.lng === locations[0].lng
        );
        boundsLatLngs?.forEach(latLng => bounds.extend(latLng));
        if (bounds) {
          map.fitBounds(bounds, mapConstants.mapPadding);
          if (allTheSame) {
            // prevents zooming in too far when all the markers are at the same location
            map.setZoom(mapConstants.zoom);
          }
        }
      }
    },
    [boundsLatLngs, mapConstants.mapPadding, mapConstants.zoom]
  );

  /**
   * Handle map load and fit to search radius, markers or center
   */
  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      if (searchRadius) {
        fitToSearchRadiusBounds(map);
      } else if (boundsLatLngs?.length) {
        fitToBoundsLatLngs(map);
      } else if (center) {
        map.setCenter(center);
      }
    },
    [boundsLatLngs, center, fitToBoundsLatLngs, fitToSearchRadiusBounds, searchRadius]
  );

  /**
   * Trigger the handleMapLoad callback when things change
   */
  useEffect(() => {
    if (mapRef.current) {
      handleMapLoad(mapRef.current);
    }
  }, [searchRadius, boundsLatLngs, center, handleMapLoad]);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  if (!isLoaded) return null;

  return (
    <GoogleMap
      aria-roledescription="map"
      center={center ?? mapConstants.center}
      mapContainerStyle={full ? containerStyleFullHeight : containerStyle}
      zoom={mapConstants.zoom}
      onLoad={handleMapLoad}
      onUnmount={onUnmount}
      options={mapOptions}
    >
      {!!searchRadius && (
        <MapSearchRadius
          {...searchRadius}
          onCenterChanged={onSearchRadiusCenterChanged}
          onRadiusChanged={onSearchRadiusRadiusChanged}
        />
      )}
      {!!Markers && Markers}
    </GoogleMap>
  );
};
